import { Link } from 'react-router-dom';
import styled from 'styled-components';
import timeSavingsImage from 'assets/images/assist/time-savings.jpg';
import mediaQuery from 'styles/mediaQuery';
import theme from 'styles/theme';
import {
  outlineCtaStyles,
  primaryCtaStyles,
  SURFACE_BLUE_SECONDARY,
  SURFACE_BLUE_SUBTLE,
  TEXT_BLUE,
  TEXT_NEUTRAL,
  TEXT_PRIMARY,
} from '../HostfullyAssist.styles';

export const OutlineCtaLink = styled.a`
  ${outlineCtaStyles}
`;

export const PrimaryCtaLink = styled(Link)`
  ${primaryCtaStyles}
`;

export const Page = styled.div`
  color: ${theme.colors.fontDark};
`;

const SectionBase = styled.section`
  padding: 64px 24px;

  ${mediaQuery.xs} {
    padding: 48px 20px;
  }
`;

export const WhiteSection = styled(SectionBase)`
  background: #fff;
`;

export const BlueSubtleSection = styled(SectionBase)`
  background: ${SURFACE_BLUE_SUBTLE};
`;

export const BlueSecondarySection = styled(SectionBase)`
  background: ${SURFACE_BLUE_SECONDARY};
`;

export const SectionInner = styled.div.attrs({ className: 'container' })`
  ${mediaQuery.xs} {
    padding: 0;
  }
`;

export const CopyStack = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  justify-content: center;
`;

export const Overline = styled.p`
  color: ${TEXT_PRIMARY};
  font-size: 1.71rem;
  font-weight: 600;
  margin: 0;

  ${mediaQuery.xs} {
    font-size: 1.42rem;
  }
`;

export const SectionHeading = styled.h2`
  font-size: 3.42rem;
  font-weight: 700;
  letter-spacing: -2%;
  line-height: 1.2;
  margin: 0;

  ${mediaQuery.xs} {
    font-size: 2rem;
  }
`;

export const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 98px;
  align-items: center;

  ${mediaQuery.xs} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

export const SplitImage = styled.img`
  aspect-ratio: 5/4;
  height: 100%;
  width: 100%;
  max-height: 495px;
  object-fit: cover;
  border-radius: 16px;
`;

export const SectionBody = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin: 0;
  white-space: pre-line;
`;

export const SectionHeader = styled.div<{ $alignStart?: boolean }>`
  align-items: ${({ $alignStart }) => ($alignStart ? 'flex-start' : 'center')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 48px;
  text-align: ${({ $alignStart }) => ($alignStart ? 'left' : 'center')};
`;

export const CardsWithVisualRow = styled.div`
  align-items: stretch;
  display: grid;
  gap: 24px;
  grid-template-columns: 3fr 2fr;

  ${mediaQuery.sm} {
    grid-template-columns: 1fr;
  }
`;

export const SideVisual = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 16px;

  ${mediaQuery.xs} {
    aspect-ratio: 5/4;
  }
`;

export const SideImage = styled.img`
  height: 100%;
  inset: 0;
  object-fit: cover;
  position: absolute;
  width: 100%;
`;

export const CardGrid = styled.div<{ $columns: number }>`
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);

  ${mediaQuery.sm} {
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQuery.xs} {
    grid-template-columns: 1fr;
  }
`;

export const CARD_BACKGROUND = '#fefefe';

export const MarketingCard = styled.div<{ $background: string }>`
  align-items: flex-start;
  background: ${({ $background }) => $background};
  border-radius: 16px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  padding: 48px 32px;
`;

export const CardIcon = styled.div`
  display: inline-flex;
  margin-bottom: 24px;
  color: ${TEXT_BLUE};

  svg {
    width: 64px;
    height: 64px;
  }

  ${mediaQuery.xs} {
    svg {
      width: 48px;
      height: 48px;
    }
  }
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

export const CardDescription = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  color: ${theme.colors.gray600};
`;

export const HeroTitle = styled.h1`
  margin: 0;
  font-size: 4rem;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: ${TEXT_PRIMARY};

  ${mediaQuery.xs} {
    font-size: 40px;
  }
`;

export const HeroSubtitle = styled.p`
  margin: 0;
  line-height: 1.2;
  font-weight: 400;
  font-size: 20px;
  letter-spacing: 0%;
  color: ${TEXT_PRIMARY};
`;

export const HeroVisual = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 64px;
    right: -24px;
    background: ${SURFACE_BLUE_SUBTLE};
    border-radius: 16px;
  }

  img {
    position: relative;
  }

  ${mediaQuery.xs} {
    padding: 24px 0;

    &::before {
      left: 24px;
      right: 0;
    }
  }
`;

export const ClosingSectionWrapper = styled(BlueSecondarySection)`
  position: relative;

  ${mediaQuery.xs} {
    padding-top: 288px;
  }
`;

export const ClosingBackdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 40%;
  background: url(${timeSavingsImage}) center / cover no-repeat;

  ${mediaQuery.xs} {
    right: 0;
    bottom: auto;
    height: 240px;
  }
`;

export const ClosingInner = styled(SectionInner)`
  position: relative;
`;

export const ClosingCard = styled.div`
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-left: auto;
  padding: 48px;
  width: min(100%, 620px);

  ${mediaQuery.xs} {
    margin: auto;
    padding: 32px 20px;
  }
`;

export const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

export const ComparisonCard = styled.div`
  background: ${SURFACE_BLUE_SUBTLE};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

export const ComparisonRow = styled.div`
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr auto 1fr;

  ${mediaQuery.xs} {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
`;

export const ComparisonSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ComparisonLabel = styled.p`
  margin: 0;
  font-size: 1.42rem;
  font-weight: 600;
  color: ${TEXT_PRIMARY};
`;

export const ComparisonValue = styled.p`
  color: ${theme.colors.hostfullyBlue};
  font-size: 2.28rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
`;

export const ComparisonNote = styled.p`
  color: ${TEXT_PRIMARY};
  font-size: 1.14rem;
  margin: 0;
`;

export const VersusLabel = styled.span`
  color: ${TEXT_PRIMARY};
  font-size: 2.28rem;
  font-weight: 600;
`;

export const ComparisonFootnote = styled.p`
  color: ${TEXT_NEUTRAL};
  font-size: 0.85rem;
  margin: 0;
`;

export const FaqRow = styled.div`
  display: grid;
  grid-template-columns: 3fr 7fr;
  gap: 48px;
  align-items: start;

  ${mediaQuery.xs} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

export const FaqList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FaqItem = styled.div`
  border-bottom: 1px solid ${SURFACE_BLUE_SUBTLE};
`;

export const FaqToggle = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${TEXT_PRIMARY};
  cursor: pointer;
  display: flex;
  font-size: 18px;
  font-weight: 600;
  gap: 16px;
  justify-content: space-between;
  line-height: 1.4;
  padding: 20px 0;
  text-align: left;
  width: 100%;
`;

export const FaqToggleSign = styled.span`
  align-items: center;
  color: ${TEXT_BLUE};
  display: inline-flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  width: 18px;
`;

export const FaqAnswer = styled.p`
  color: ${theme.colors.gray600};
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
  padding: 0 40px 20px 0;
  white-space: pre-line;

  a {
    color: ${theme.colors.hostfullyBlue};
    text-decoration: underline;
  }
`;

export const FaqViewMoreButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: none;
  border: none;
  color: ${theme.colors.hostfullyBlue};
  cursor: pointer;
  display: inline-flex;
  font-size: 16px;
  font-weight: 600;
  gap: 8px;
  padding: 20px 0 0;

  svg {
    transition: transform 0.2s ease-in-out;
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }
`;

export const InboxRow = styled.div`
  align-items: stretch;
  display: grid;
  gap: 32px;
  grid-template-columns: 1fr 1fr;

  ${mediaQuery.xs} {
    grid-template-columns: 1fr;
  }
`;

export const InboxColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const InboxVisual = styled.div`
  aspect-ratio: 151 / 152;
  flex: 1 0 auto;
  position: relative;
`;

export const InboxImage = styled.img`
  height: 100%;
  inset: 0;
  object-fit: cover;
  object-position: bottom;
  position: absolute;
  width: 100%;
`;
